import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface UseFeedPagingParams {
  itemCount: number;
  initialIndex: number;
}

interface UseFeedPagingResult {
  scrollRef: React.RefObject<HTMLDivElement>;
  setSlideRef: (index: number) => (el: HTMLDivElement | null) => void;
  activeIndex: number;
  hasUserScrolled: boolean;
}

const ACTIVE_SLIDE_THRESHOLD = 0.6;

const useFeedPaging = ({
  itemCount,
  initialIndex,
}: UseFeedPagingParams): UseFeedPagingResult => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  const slideRefCallbacks = useRef(
    new Map<number, (el: HTMLDivElement | null) => void>()
  );

  const setSlideRef = useCallback((index: number) => {
    let callback = slideRefCallbacks.current.get(index);
    if (!callback) {
      callback = (el: HTMLDivElement | null) => {
        slideRefs.current[index] = el;
      };
      slideRefCallbacks.current.set(index, callback);
    }
    return callback;
  }, []);

  // Pin the feed to initialIndex until the user scrolls. Several layout timing
  // traps make a one-shot scroll fail:
  //  1. initialIndex can change a frame or two after mount (activeTabSlug /
  //     entrySlug / sections settle across renders), so we re-run on change.
  //  2. The DS Overlay settles its layout across several frames after mount. On
  //     early passes the scroll container reports a non-zero-but-wrong
  //     clientHeight (e.g. 2307 before it lands on 922) AND the slides haven't
  //     reached full height yet, so scrollHeight ≈ clientHeight and there is
  //     nothing to scroll — a scrollTop assignment silently clamps to 0. A
  //     "clientHeight !== 0" guard is not enough; we also require real scroll
  //     range before assigning.
  //  3. Because clientHeight is transiently wrong, the correct pixel offset only
  //     exists once layout is stable, so we re-pin on every resize (recomputing
  //     index * clientHeight) rather than latching after one attempt — an
  //     intermediate landing self-corrects when the height settles.
  // We also center virtualization on initialIndex up front so the target slide's
  // media is mounted even before the scroll lands. Programmatic scrollTop fires
  // no touchmove/wheel, so it never trips hasUserScrolled; once the user does
  // scroll, the dep change tears the observer down.
  useLayoutEffect(() => {
    if (hasUserScrolled || itemCount === 0) return undefined;
    const root = scrollRef.current;
    if (!root) return undefined;

    const targetIndex = Math.min(Math.max(initialIndex, 0), itemCount - 1);
    setActiveIndex(targetIndex);

    const pin = (): void => {
      if (hasUserScrolled || root.clientHeight === 0) return;

      const desired = targetIndex * root.clientHeight;
      // Content not laid out yet — scroll range can't reach the target.
      if (root.scrollHeight - root.clientHeight < desired) return;

      if (Math.abs(root.scrollTop - desired) > 1) root.scrollTop = desired;
    };

    pin();

    // Keep re-pinning as the Overlay settles (clientHeight/scrollHeight change),
    // until the user scrolls (dep change disconnects this observer).
    const observer = new ResizeObserver(() => pin());
    observer.observe(root);
    return () => observer.disconnect();
  }, [itemCount, initialIndex, hasUserScrolled]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || itemCount === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(
              (entry.target as HTMLElement).dataset.index ?? '0'
            );
            setActiveIndex(index);
          }
        });
      },
      { root, threshold: ACTIVE_SLIDE_THRESHOLD }
    );
    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [itemCount]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || itemCount === 0) return undefined;
    const markUserScrolled = () => setHasUserScrolled(true);
    root.addEventListener('touchmove', markUserScrolled, { passive: true });
    root.addEventListener('wheel', markUserScrolled, { passive: true });
    return () => {
      root.removeEventListener('touchmove', markUserScrolled);
      root.removeEventListener('wheel', markUserScrolled);
    };
  }, [itemCount]);

  return { scrollRef, setSlideRef, activeIndex, hasUserScrolled };
};

export default useFeedPaging;
